import { formatBytes } from "@/lib/format";
import { cn } from "@/lib/utils";

interface UploadProgressBarProps {
  /** null = no upload in flight; 0–100 = percent; -1 = length unknown. */
  progress: number | null;
  /** When provided, the "X of Y" counter is rendered on the right side. */
  totalBytes?: number;
}

/**
 * Thin two-line progress display used by the upload dialogs.
 * Returns null when `progress` is null so callers can mount it
 * unconditionally without guarding.
 */
export function UploadProgressBar({ progress, totalBytes }: UploadProgressBarProps) {
  if (progress === null) return null;
  const indeterminate = progress < 0;

  return (
    <div className="space-y-1">
      <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
        <div
          className={cn(
            "h-full bg-primary transition-[width] duration-150",
            indeterminate && "w-1/3 animate-pulse",
          )}
          style={!indeterminate ? { width: `${progress}%` } : undefined}
        />
      </div>
      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <span>{indeterminate ? "Uploading…" : `Uploading… ${progress}%`}</span>
        {!indeterminate && totalBytes != null && (
          <span className="font-mono tabular-nums">
            {formatBytes(Math.round((progress / 100) * totalBytes))} of {formatBytes(totalBytes)}
          </span>
        )}
      </div>
    </div>
  );
}
